import type { Messages } from './es'
import { enCommon } from './namespaces/common.en'
import { enBridge, enPoolUi, enProfile, enSwapUi, enTokenDetail, enTradeSettings } from './namespaces/trading.en'
import {
  enExplore,
  enHomeDashboard,
  enPortfolio,
  enShell,
  enWallet,
} from './namespaces/ui.en'

export const enMessages: Messages = {
  brand: {
    tagline: 'INTERGALACTIC DEX',
    infoMenu: 'Information',
    goToSwap: 'Go to Swap',
    commandPaletteHint: 'Press ⌘K to open the command palette',
  },
  nav: {
    home: 'Home',
    swap: 'Swap',
    explore: 'Explore',
    portfolio: 'Portfolio',
    bridge: 'Bridge',
    settings: 'Settings',
    about: 'About us',
    legal: 'Jurisdiction',
    contact: 'Contact',
    designSystem: 'Design System',
    navigation: 'Navigation',
    application: 'Application',
    information: 'Information',
    internal: 'Internal',
  },
  pageTitles: {
    home: 'Home',
    explore: 'Explore',
    portfolio: 'Portfolio',
    movements: 'Activity',
    pool: 'Pool',
    positions: 'My positions',
    token: 'Token',
    about: 'About us',
    legal: 'Jurisdiction',
    contact: 'Contact',
    bridge: 'Bridge',
    designSystem: 'Design System',
    settings: 'Settings',
    markets: 'Markets',
    fallback: "Don't Panic",
  },
  pageHeaders: {
    explore: {
      subtitle:
        'Browse tokens, markets, and pools across the ecosystem. Each listing opens its detail page before you trade.',
    },
    markets: {
      subtitle:
        'Browse spot pairs across the ecosystem and open each token page to go deeper.',
    },
    tokenDetail: {
      subtitle:
        'Review contract, market, associated liquidity, chart, and official links before you trade.',
    },
    swap: {
      subtitle: 'Execute spot swaps with quotes from V2 pools across the ecosystem.',
    },
    liquidityEcosystem: {
      subtitle:
        'Discover active pools, compare aggregate metrics, and open each pair detail page.',
    },
    poolDetail: {
      subtitle:
        'Review reserves, pair metrics, and on-chain contracts before adding or removing liquidity.',
    },
    liquidityPositions: {
      subtitle:
        'Monitor your LP exposure, pick a position, and access management flows.',
    },
    addLiquidity: {
      subtitle: 'Add tokens to an existing pair and receive LP tokens based on your share.',
    },
    importPool: {
      subtitle: 'Recover missing LP pairs in your list so you can manage them from the platform.',
    },
    removeLiquidity: {
      subtitle:
        'Withdraw part or all of your share and receive the underlying tokens plus accrued fees.',
    },
    portfolio: {
      subtitle: 'Consolidated view of balances and assets in your connected wallet.',
    },
    portfolioConnect: {
      subtitle: 'Connect your wallet to unlock balances, assets, and your portfolio summary.',
    },
    portfolioMovements: {
      subtitle: 'Chronological log of operations executed from this platform.',
    },
    about: {
      subtitle:
        "What Don't Panic is, the Nutri-Matic, our principles, and the notice before you trade.",
    },
    legal: {
      subtitle:
        'Manifestos that declare our jurisdiction: cyberspace. Read them, download them, or pick a language.',
    },
    contact: {
      subtitle: 'Follow us on social and join the conversation on X and Telegram.',
    },
    bridge: {
      subtitle: 'Reserved space for cross-chain transfers when the logic becomes available.',
    },
    profile: {
      subtitle: 'Identity and connected wallet on the platform.',
    },
    designSystem: {
      subtitle:
        'Internal reference for components, visual tokens, and interface patterns in the product.',
    },
    settings: {
      subtitle:
        'Adjust trading preferences, language, and manage your wallet connection in one place.',
    },
  },
  home: {
    subtitle: 'THE INTERGALACTIC DECENTRALIZED EXCHANGE',
    lead:
      "A mostly harmless financial protocol for trading across the known universe. Powered by the Heart of Gold's Infinite Improbability Drive technology.",
    tryNutrimatic: 'Try Nutri-Matic',
    whatIsDontPanic: "What is Don't Panic",
    exploreEcosystem: 'Explore the ecosystem',
    joinConversation: 'Join the conversation',
    joinLead: "Follow our updates and be part of the Don't Panic community.",
    followX: 'Follow on X',
    joinTelegram: 'Join the channel',
  },
  about: {
    headerSub: 'Decentralized exchange platform on Infinite Drive',
    meaningTitle: "What does Don't Panic mean?",
    meaningLead:
      "Don't Panic means don't be alarmed — in large, friendly letters, like on the cover of The Hitchhiker's Guide to the Galaxy.",
    whatIsTitle: "What is Don't Panic",
    whatIsP1:
      "Don't Panic is a decentralized exchange platform for trading on Infinite Drive. A mostly harmless financial protocol, powered by the Heart of Gold's Infinite Improbability Drive, that makes on-chain trading, liquidity, and market exploration accessible.",
    whatIsP2:
      "We are not a bank, broker, or custodian. We are a platform for you to trade with your own keys — with the calm that belongs to anyone who knows where their towel is.",
    nutrimaticTitle: 'The Nutri-Matic',
    nutrimaticP1:
      "The Nutri-Matic is Don't Panic's digital asset synthesizer: the booth where you swap, review quotes, and confirm transactions. It is inspired by the Heart of Gold's legendary dispenser — famous for producing liquids \"almost, but not quite, entirely unlike tea.\" This version delivers what you ask for.",
    nutrimaticP2:
      'The Nutri-Matic does not power the protocol; it is the device you interact with. The engine is the Infinite Improbability Drive; the network is Infinite Drive.',
    principlesTitle: 'Principles',
    principles: [
      {
        title: 'Self-custody',
        copy: 'Your keys, your assets. We do not custody funds or have access to your wallet.',
      },
      {
        title: 'On-chain and verifiable',
        copy: 'Swaps and liquidity run through contracts deployed on Infinite Drive.',
      },
      {
        title: 'Built to operate',
        copy: 'Trading, pools, and portfolio accessible from desktop and mobile.',
      },
    ],
    noticeTitle: 'Important notice',
    noticeP1:
      "Don't Panic is mostly harmless, but DeFi does not reward the distracted traveler either. Do your own research before you trade. This platform does not provide financial advice or asset custody.",
    noticeP2Prefix:
      'Our jurisdiction is cyberspace — not a territory with judges and folders, but the digital framework in which we declare how we understand sovereignty, privacy, and commerce without intermediaries.',
    noticeLegalLink: 'Read the manifestos in Jurisdiction',
    relatedLinksAria: 'Related links',
    linkLegal: 'Jurisdiction',
    linkContact: 'Contact',
    linkHome: 'Back to home',
  },
  contact: {
    lead:
      "Follow our updates, join the conversation, and be the first to know what's coming to Don't Panic.",
    xCaption: 'Announcements and ecosystem news',
    telegramCaption: 'Community, alerts, and conversation',
    followX: 'Follow on X',
    joinTelegram: 'Join the channel',
    firstTimePrefix: 'First time here?',
    firstTimeLink: 'Learn about the platform',
    footerNote: 'External links open in a new tab. For foundational texts, see',
    footerLegalLink: 'Jurisdiction',
  },
  settings: {
    title: 'Settings',
    sectionsNavAria: 'Settings sections',
    sections: {
      account: {
        label: 'Account',
        description: 'Connected wallet status and session options.',
      },
      language: {
        label: 'Language',
        description: 'Choose the platform language or leave it on automatic based on your browser.',
      },
      trade: {
        label: 'Trade',
        description: 'Slippage and deadline for spot swaps.',
      },
    },
    language: {
      preferenceLabel: 'Language preference',
      auto: 'Automatic',
      autoHint: 'Detected automatically from your browser\'s language preferences for websites.',
      es: 'Español',
      en: 'English',
    },
  },
  legal: {
    localeLabel: 'Language',
    localeSelectorAria: 'Select document language',
    intro:
      'Foundational cyberspace texts — Declaration of the Independence of Cyberspace, Crypto Anarchist Manifesto and Cypherpunk Manifesto. Each document includes a link to its original publication.',
    footerNote:
      "These manifestos are not financial advice. For product context — Don't Panic, the Nutri-Matic and how we operate — see",
    footerAbout: 'About us',
    footerOr: 'or',
    footerContact: 'Contact',
  },
  common: enCommon,
  wallet: enWallet,
  portfolio: enPortfolio,
  explore: enExplore,
  homeDashboard: enHomeDashboard,
  shell: enShell,
  swapUi: enSwapUi,
  poolUi: enPoolUi,
  tokenDetail: enTokenDetail,
  bridge: enBridge,
  profile: enProfile,
  tradeSettings: enTradeSettings,
  settingsAccount: {
    description:
      'Check if your wallet is connected to the correct network. From here you can open the account panel, switch network or disconnect the active session.',
  },
}
